import { useState, useCallback, type ReactNode } from 'react';

const STORAGE_PREFIX = 'anima:hint:';

interface OnboardingHintProps {
  /** Уникальный ID хинта (для localStorage) */
  hintId: string;
  /** Текст подсказки */
  children: ReactNode;
  /** Иконка слева (emoji или SVG) */
  icon?: string;
  className?: string;
}

/**
 * OnboardingHint — dismissable подсказка для новых пользователей.
 * Запоминает закрытие в localStorage, не показывается повторно.
 */
export function OnboardingHint({
  hintId,
  children,
  icon = '💡',
  className = '',
}: OnboardingHintProps): JSX.Element | null {
  const storageKey = `${STORAGE_PREFIX}${hintId}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; }
    catch { return false; }
  });

  const dismiss = useCallback(() => {
    try { localStorage.setItem(storageKey, '1'); }
    catch { /* Private browsing / quota — dismiss only in-memory */ }
    setDismissed(true);
  }, [storageKey]);

  if (dismissed) return null;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-card border border-accent-brand/20 bg-accent-brand/5 ${className}`}
    >
      <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 text-sm text-text-primary leading-relaxed">
        {children}
      </div>
      <button
        onClick={dismiss}
        className="flex-shrink-0 p-1 text-text-secondary hover:text-text-primary transition-colors rounded"
        aria-label="Скрыть подсказку"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
